/**
 * WebSocket Orders Hook
 *
 * Liga o stream de pedidos ao React Query.
 *
 * O modelo é híbrido: mudanças de status chegam pelo WebSocket e são aplicadas
 * localmente (sem HTTP), enquanto pedido novo e remoção disparam recarga —
 * porque o evento não carrega produtos nem valor.
 *
 * Três travas garantem que a tela não divirja do banco:
 *
 *   1. Reconexão sempre recarrega. Eventos ocorridos com o socket fora não têm
 *      replay nem número de sequência: seriam perdidos em silêncio.
 *   2. Eventos que chegam durante uma carga HTTP ficam retidos no serviço e são
 *      aplicados depois, fechando a corrida entre snapshot e stream.
 *   3. Rede de segurança: recarga periódica e ao voltar o foco da janela,
 *      reconciliando qualquer divergência residual.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getWebSocketOrderService } from '@services/api/websocketService';
import type { OrderStatusOverlay } from '@services/api/websocketService';
import type { WebSocketConnectionStatus } from '@services/api/orderTypes';
import { wsLog } from '@utils/wsLogger';
import { ORDER_KEYS } from './keys';

/** Agrupa rajadas de created/deleted numa única recarga. */
const RESYNC_DEBOUNCE_MS = 500;

/** Reconciliação periódica, mesmo sem nenhum evento. */
const SAFETY_RESYNC_MS = 60_000;

export interface UseWebSocketOrdersParams {
  establishmentId: string;
  intervalId?: string;
  enabled?: boolean;
}

interface UseWebSocketOrdersReturn {
  /** Overlays de status a sobrepor aos pedidos carregados por HTTP. */
  orders: OrderStatusOverlay[];
  isLoading: boolean;
  error: Error | null;
  connectionStatus: WebSocketConnectionStatus;
  isConnected: boolean;
  isConnecting: boolean;
  lastUpdated: string | null;
  reconnect: () => void;
  disconnect: () => void;
  /** Mantido por compatibilidade com quem já consumia este retorno. */
  rawOrders: OrderStatusOverlay[];
}

export function useWebSocketOrders({
  establishmentId,
  intervalId,
  enabled = true,
}: UseWebSocketOrdersParams): UseWebSocketOrdersReturn {
  const queryClient = useQueryClient();
  const serviceRef = useRef(getWebSocketOrderService());

  const [connectionStatus, setConnectionStatus] = useState<WebSocketConnectionStatus>('disconnected');
  const [overlays, setOverlays] = useState<OrderStatusOverlay[]>([]);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);

  const resyncTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Recarrega os pedidos via HTTP. O serviço é avisado do início e do fim para
  // reter os eventos que chegarem no meio e descartar os overlays que a
  // resposta já incorpora.
  const resync = useCallback(
    (reason: string) => {
      if (resyncTimer.current) clearTimeout(resyncTimer.current);

      resyncTimer.current = setTimeout(() => {
        resyncTimer.current = null;
        const service = serviceRef.current;
        const startedAt = service.beginSnapshot();

        wsLog.debug('[ws] recarregando pedidos:', reason);

        queryClient
          .invalidateQueries({ queryKey: [ORDER_KEYS.LIST_ORDERS] })
          .catch((error) => wsLog.error('[ws] falha ao recarregar pedidos', error))
          .finally(() => {
            service.commitSnapshot(startedAt);
            setOverlays(service.getOverlays());
            setLastUpdated(new Date().toISOString());
          });
      }, RESYNC_DEBOUNCE_MS);
    },
    [queryClient],
  );

  // Conexão e assinatura
  useEffect(() => {
    const service = serviceRef.current;

    if (!enabled || !establishmentId) {
      service.disconnect();
      return;
    }

    const unsubscribe = service.subscribe((next) => {
      setOverlays(next);
      setLastUpdated(new Date().toISOString());
    });

    service.connect({
      establishmentId,
      intervalId,
      payload: 'raw',
      onStatusChange: setConnectionStatus,
      onResyncNeeded: resync,
      onError: (error) => wsLog.error('[ws]', error.message),
    });

    return () => {
      unsubscribe();
      service.disconnect();
    };
  }, [enabled, establishmentId, intervalId, resync]);

  // Rede de segurança: reconcilia periodicamente e ao voltar o foco.
  useEffect(() => {
    if (!enabled || !establishmentId) return;

    const interval = setInterval(() => resync('rede de segurança'), SAFETY_RESYNC_MS);

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        resync('janela voltou ao foco');
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled, establishmentId, resync]);

  // Limpa o debounce pendente ao desmontar
  useEffect(() => {
    return () => {
      if (resyncTimer.current) clearTimeout(resyncTimer.current);
    };
  }, []);

  const reconnect = useCallback(() => {
    const service = serviceRef.current;
    service.disconnect();
    if (enabled && establishmentId) {
      service.connect({
        establishmentId,
        intervalId,
        payload: 'raw',
        onStatusChange: setConnectionStatus,
        onResyncNeeded: resync,
      });
    }
  }, [enabled, establishmentId, intervalId, resync]);

  const disconnect = useCallback(() => {
    serviceRef.current.disconnect();
  }, []);

  const isConnected = connectionStatus === 'connected';
  const isConnecting = connectionStatus === 'connecting';

  return {
    orders: overlays,
    isLoading: isConnecting,
    error: connectionStatus === 'error' ? new Error('Falha na conexão em tempo real') : null,
    connectionStatus,
    isConnected,
    isConnecting,
    lastUpdated,
    reconnect,
    disconnect,
    rawOrders: overlays,
  };
}

/**
 * Carga inicial de pedidos via HTTP (uma vez, sem polling).
 */
export function useInitialOrders({ establishmentId, enabled = true }: { establishmentId: string; enabled?: boolean }) {
  return useQuery({
    queryKey: [ORDER_KEYS.LIST_ORDERS, 'initial', establishmentId],
    queryFn: async () => {
      const { orderService } = await import('@services/api');
      return await orderService.getTodayOrders({ establishment_id: establishmentId });
    },
    enabled: enabled && !!establishmentId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export default useWebSocketOrders;
